import { ControlledDropDown } from '@ui-kit'
import { Control, UseFormSetValue } from 'react-hook-form'
import { AllFieldsType } from './RequestorSearchForm'
import {
  DropdownType,
  getCategoryDropdownOptions,
  getEntityDataByEntityNameApiForFilter,
  GetEntityDataByNameIdFilter,
  getEntityList,
} from '@app-store'
import { useEffect, useState } from 'react'
import {
  componentNames,
  CUSTOM_SEPERATOR,
  NAMESPACES,
} from '../../utils/constants'
import { get } from 'lodash'
import { RECORD_STATUS } from '../../constants/form.constants'

interface DropdownFieldsType {
  label: string
  name: keyof AllFieldsType
  control: Control<AllFieldsType>
  api_name: string
  embedded_name?: string
  tabIndex?: number
  disabled?: boolean
  selectedValue?: string | null
  filterBy?: string
  uniqueValues?: boolean
  setValue: UseFormSetValue<AllFieldsType>
  optionsBy?: string
  isDependent?: boolean
  payload?: GetEntityDataByNameIdFilter
}

const DropdownFields = ({
  label,
  name,
  control,
  api_name,
  embedded_name,
  tabIndex,
  disabled = false,
  selectedValue,
  filterBy,
  uniqueValues,
  setValue,
  optionsBy,
  isDependent,
  payload,
}: DropdownFieldsType) => {
  const [dropdownList, setDropdownList] = useState<DropdownType[]>([])
  const fetchList = async () => {
    try {
      if (api_name === componentNames.CATEGORY) {
        const response = await getCategoryDropdownOptions(embedded_name ?? '')
        if (response.data) {
          setDropdownList(
            response.data
              .map((item: { id: number; value: string }) => ({
                value:
                  optionsBy === NAMESPACES.LABEL_KEY
                    ? item.value
                    : item.id.toString(),
                label: item.value,
              }))
              ?.sort((a: DropdownType, b: DropdownType) =>
                a.label.localeCompare(b.label)
              )
          )
        }
      } else {
        const response = payload
          ? await getEntityDataByEntityNameApiForFilter(payload)
          : await getEntityList({
              entity_name: api_name,
              embedded_name: embedded_name ?? 'name',
            })
        if (selectedValue && filterBy && isDependent) {
          const selectedValues = selectedValue.split(',')
          const selectedFilters = filterBy.split(',')

          const embeddedNames = embedded_name ? embedded_name.split(',') : []
          const primaryEmbedded = embeddedNames[0]

          const idKey = primaryEmbedded?.includes('.')
            ? `${primaryEmbedded.split('.')[0]}.id`
            : 'id'

          const filtered = response.data.content.filter(
            (item: (typeof response.data.content)[0]) => {
              for (let i = 0; i < selectedValues.length; i++) {
                const val = selectedValues[i]
                const key = selectedFilters[i]

                if (!val || val === 'null' || val === 'undefined') continue

                const itemValue = (get(item, key) ?? '').toString()
                if (itemValue !== val.toString()) return false
              }

              return item.record_status !== RECORD_STATUS.INACTIVE
            }
          )

          const labelMap: Record<string, { value: string; label: string }> = {}

          filtered.forEach((item: { name: string }) => {
            const id =
              optionsBy === NAMESPACES.LABEL_KEY
                ? get(item, primaryEmbedded)
                : get(item, idKey)?.toString()

            const label = primaryEmbedded
              ? get(item, primaryEmbedded)
              : item.name

            const strId = id?.toString() ?? ''

            if (!labelMap[label]) {
              labelMap[label] = {
                value: strId,
                label,
              }
            } else {
              const existingIds = labelMap[label].value.split(
                CUSTOM_SEPERATOR.SAFE_DELIMITER
              )
              if (!existingIds.includes(strId)) {
                labelMap[
                  label
                ].value = `${labelMap[label].value}${CUSTOM_SEPERATOR.SAFE_DELIMITER}${strId}`
              }
            }
          })
          const list: DropdownType[] = Object.values(labelMap).sort((a, b) =>
            a.label.localeCompare(b.label)
          )

          setDropdownList(list)
        } else if (selectedValue && filterBy) {
          setDropdownList(
            response.data.content
              .filter(
                (item: (typeof response.data.content)[0]) =>
                  (get(item, filterBy) ?? '').toString() === selectedValue &&
                  item.record_status !== RECORD_STATUS.INACTIVE
              )
              .map((item: (typeof response.data.content)[0]) => {
                let idKey = 'id'
                if (embedded_name?.split(',')[0].includes('.')) {
                  idKey = embedded_name.split(',')[0].split('.')[0] + '.id'
                }
                return {
                  value:
                    optionsBy === NAMESPACES.LABEL_KEY
                      ? embedded_name
                        ? get(item, embedded_name.split(',')[0])
                        : item.name
                      : get(item, idKey).toString(),
                  label: embedded_name
                    ? get(item, embedded_name.split(',')[0])
                    : item.name,
                }
              })
              ?.sort((a: DropdownType, b: DropdownType) =>
                a.label.localeCompare(b.label)
              )
          )
        } else if (filterBy && !selectedValue) {
          setDropdownList([])
        } else {
          const newDropdownList: DropdownType<string, string>[] = []
          if (uniqueValues) {
            const optionsList = response?.data?.content ?? []
            if (optionsList?.length) {
              const uniqueValueSet = new Set<string>()
              for (const item of optionsList) {
                if (item.record_status === RECORD_STATUS.INACTIVE) continue
                const name = item?.[embedded_name ?? 'name']
                if (!uniqueValueSet.has(name)) {
                  uniqueValueSet.add(name)
                  if (name !== null)
                    newDropdownList.push({ label: name, value: name })
                }
              }
            }
            setDropdownList(
              [...newDropdownList]?.sort((a: DropdownType, b: DropdownType) =>
                a.label.localeCompare(b.label)
              )
            )
          } else
            setDropdownList(
              response?.data?.content
                ?.filter((item: (typeof response.data.content)[0]) => {
                  return item.record_status !== RECORD_STATUS.INACTIVE
                })
                ?.map((item: (typeof response.data.content)[0]) => {
                  return {
                    value:
                      embedded_name === 'email,first_name,last_name' &&
                      optionsBy === NAMESPACES.LABEL_KEY
                        ? `${item?.first_name} ${item?.last_name}`
                        : optionsBy === NAMESPACES.LABEL_KEY
                        ? item?.[embedded_name ?? 'name']
                        : item?.id?.toString(),
                    label:
                      embedded_name === 'email,first_name,last_name' &&
                      optionsBy === NAMESPACES.LABEL_KEY
                        ? item.email
                        : item?.[embedded_name ?? 'name'],
                  }
                })
                ?.sort((a: DropdownType, b: DropdownType) =>
                  a.label.localeCompare(b.label)
                )
            )
        }
      }
    } catch (err) {
      console.error(err)
    }
  }
  const emptyDependentFields = (name: string) => {
    switch (name) {
      case 'services__id':
        setValue('case_summary__outcome_id', '')
        setValue('case_status_id', '')
        setValue('case_summary__outcome_reason_id', '')
        return

      case 'c_sub_services__name':
        setValue('case_summary__outcome_id', '')
        setValue('case_summary__outcome_reason_id', '')
        return

      case 'c_case_statuses__overall_case_status':
        setValue('case_status_id', '')
        return

      case 'case_summary__outcome_id': {
        setValue('case_summary__outcome_reason_id', '')
        return
      }

      case 'case_type':
        setValue('outcome', '')
        setValue('case_status', '')
        setValue('outcome_reason', '')
        return

      case 'case_subtype':
        setValue('outcome', '')
        setValue('outcome_reason', '')
        return

      case 'overall_case_status':
        setValue('case_status', '')
        return

      case 'outcome': {
        setValue('outcome_reason', '')
        return
      }
    }
  }
  useEffect(() => {
    fetchList()
  }, [selectedValue])

  return (
    <ControlledDropDown
      label={label}
      options={dropdownList}
      name={name}
      onChange={() => {
        emptyDependentFields(name)
      }}
      control={control}
      tabIndex={tabIndex ?? 0}
      isSearchable={true}
      disabled={disabled}
    />
  )
}

export default DropdownFields
